import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Search, X, ChevronRight } from "lucide-react";

/**
 * Users section — list + filters + side drawer with full profile/projects/payments/sessions.
 * Row actions: change tier, suspend/unsuspend, delete (typed confirmation).
 * Every mutation goes through /api/admin/users/{id} which writes to admin_audit_log.
 *
 * Strict readiness everywhere: typeof checks, no truthy-only shortcuts.
 */

const TIERS = ["", "free", "basic", "plus", "pro"];
const STATUSES = ["", "active", "suspended", "blocked"];

export default function AdminUsers() {
  const [list, setList] = useState(null); // null = loading; {users:[]} when loaded
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: "", tier: "", status: "" });
  const [drawerUserId, setDrawerUserId] = useState(null);

  const fetchList = useCallback(() => {
    setList(null);
    const params = { page, limit: 50 };
    if (filters.search) params.search = filters.search;
    if (filters.tier) params.tier = filters.tier;
    if (filters.status) params.status = filters.status;
    apiClient
      .get("/admin/users", { params })
      .then((res) => setList(res.data))
      .catch((err) => setError(err.response?.data?.detail || "Failed to load users"));
  }, [page, filters]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onAction = () => {
    // refetch list and drawer
    fetchList();
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="admin-users">
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Users</h1>
        <p className="text-sm text-slate-500 mt-1">Search, filter, and manage user accounts.</p>
      </header>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by email"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(1);
                fetchList();
              }
            }}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="admin-users-search"
          />
        </div>
        <select
          value={filters.tier}
          onChange={(e) => {
            setFilters((f) => ({ ...f, tier: e.target.value }));
            setPage(1);
          }}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          data-testid="admin-users-filter-tier"
        >
          {TIERS.map((t) => (
            <option key={t || "all"} value={t}>
              {t ? `Tier: ${t}` : "All tiers"}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => {
            setFilters((f) => ({ ...f, status: e.target.value }));
            setPage(1);
          }}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          data-testid="admin-users-filter-status"
        >
          {STATUSES.map((s) => (
            <option key={s || "all"} value={s}>
              {s ? `Status: ${s}` : "All statuses"}
            </option>
          ))}
        </select>
        <button
          onClick={() => {
            setPage(1);
            fetchList();
          }}
          className="px-3 py-2 bg-slate-900 text-white rounded-lg text-sm"
          data-testid="admin-users-apply-filters"
        >
          Apply
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm mb-4">
          {error}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {list === null ? (
          <TableSkeleton />
        ) : list.users.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No users match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-left">
                <tr>
                  <Th>Email</Th>
                  <Th>Tier</Th>
                  <Th>Status</Th>
                  <Th>Signup</Th>
                  <Th>Last login</Th>
                  <Th>Generations</Th>
                  <Th>Lifetime $</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {list.users.map((u) => (
                  <tr
                    key={u.user_id}
                    onClick={() => setDrawerUserId(u.user_id)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    data-testid={`admin-users-row-${u.user_id}`}
                  >
                    <Td className="font-medium text-slate-800">{u.email}</Td>
                    <Td><TierBadge tier={u.tier} /></Td>
                    <Td><StatusBadge status={u.status} /></Td>
                    <Td>{fmtDate(u.signup_date)}</Td>
                    <Td>{fmtLastLogin(u.last_login)}</Td>
                    <Td>{typeof u.total_generations === "number" ? u.total_generations : 0}</Td>
                    <Td>${(u.lifetime_revenue ?? 0).toFixed(2)}</Td>
                    <Td><ChevronRight className="w-4 h-4 text-slate-400" /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {list !== null && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
          <span>
            {list.total} users · page {list.page}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40"
              data-testid="admin-users-prev-page"
            >
              Previous
            </button>
            <button
              disabled={page * list.limit >= list.total}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40"
              data-testid="admin-users-next-page"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {drawerUserId && (
        <UserDrawer userId={drawerUserId} onClose={() => setDrawerUserId(null)} onAction={onAction} />
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 font-medium">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}

function TierBadge({ tier }) {
  const safe = typeof tier === "string" && tier.length > 0 ? tier : "free";
  const colors = {
    free: "bg-slate-100 text-slate-700",
    basic: "bg-blue-100 text-blue-700",
    plus: "bg-violet-100 text-violet-700",
    pro: "bg-amber-100 text-amber-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${colors[safe] || colors.free}`}>
      {safe}
    </span>
  );
}
function StatusBadge({ status }) {
  const safe = typeof status === "string" && status.length > 0 ? status : "active";
  const colors = {
    active: "bg-green-100 text-green-700",
    suspended: "bg-yellow-100 text-yellow-800",
    blocked: "bg-red-100 text-red-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${colors[safe] || colors.active}`}>
      {safe}
    </span>
  );
}

function fmtDate(v) {
  if (typeof v !== "string" || v.length < 10) return "—";
  try {
    return new Date(v).toLocaleDateString();
  } catch {
    return v.slice(0, 10);
  }
}

function fmtLastLogin(v) {
  // Explicit "Never" for never-logged-in users instead of the generic "—".
  if (v === null || v === undefined || (typeof v === "string" && v.length === 0)) return "Never";
  if (typeof v !== "string" || v.length < 10) return "Never";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return "Never";
    // Show relative time if < 7 days
    const ageMs = Date.now() - d.getTime();
    const days = Math.floor(ageMs / 86400000);
    if (days < 1) return "Today";
    if (days < 2) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    return d.toLocaleDateString();
  } catch {
    return "Never";
  }
}

function TableSkeleton() {
  return (
    <div className="p-2" data-testid="admin-users-loading">
      {[1, 2, 3, 4, 5, 6, 7].map((i) => (
        <div key={i} className="h-12 bg-slate-100 rounded animate-pulse my-2 mx-2" />
      ))}
    </div>
  );
}

// ---------- Drawer -------------------------------------------------------------

function UserDrawer({ userId, onClose, onAction }) {
  const [detail, setDetail] = useState(null);
  const [modal, setModal] = useState(null); // {type: 'tier'|'suspend'|'unsuspend'|'delete'}

  const fetchDetail = useCallback(() => {
    setDetail(null);
    apiClient
      .get(`/admin/users/${userId}`)
      .then((res) => setDetail(res.data))
      .catch((err) => toast.error(err.response?.data?.detail || "Failed to load user detail"));
  }, [userId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const ready =
    typeof detail === "object" &&
    detail !== null &&
    typeof detail.profile === "object" &&
    detail.profile !== null &&
    typeof detail.profile.email === "string" &&
    detail.profile.email.length > 0;

  const profile = ready ? detail.profile : null;
  const status = typeof profile?.account_status === "string" && profile.account_status.length > 0
    ? profile.account_status
    : "active";

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-user-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-xl">
        <header className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs text-slate-500">User</div>
            <div className="font-semibold text-slate-900 truncate">
              {ready ? profile.email : <span className="inline-block h-4 w-48 bg-slate-200 rounded animate-pulse" />}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100"
            data-testid="admin-user-drawer-close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {!ready ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* actions */}
            <div className="flex flex-wrap gap-2">
              <button
                className="px-3 py-2 bg-violet-600 text-white rounded-lg text-sm"
                onClick={() => setModal({ type: "tier" })}
                data-testid="admin-user-action-tier"
              >
                Change tier
              </button>
              {status === "suspended" ? (
                <button
                  className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm"
                  onClick={() => setModal({ type: "unsuspend" })}
                  data-testid="admin-user-action-unsuspend"
                >
                  Unsuspend
                </button>
              ) : (
                <button
                  className="px-3 py-2 bg-yellow-500 text-white rounded-lg text-sm"
                  onClick={() => setModal({ type: "suspend" })}
                  data-testid="admin-user-action-suspend"
                >
                  Suspend
                </button>
              )}
              <button
                className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm ml-auto"
                onClick={() => setModal({ type: "delete" })}
                data-testid="admin-user-action-delete"
              >
                Delete user
              </button>
            </div>

            {/* profile */}
            <Section title="Profile">
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <Field k="User ID" v={profile.user_id} mono />
                <Field k="Tier" v={<TierBadge tier={profile.subscription_tier} />} />
                <Field k="Status" v={<StatusBadge status={status} />} />
                <Field k="Auth provider" v={profile.auth_provider} />
                <Field k="Email verified" v={profile.email_verified ? "Yes" : "No"} />
                <Field k="Created" v={fmtDate(profile.created_at)} />
                <Field k="Last login" v={fmtDate(profile.last_login)} />
                <Field k="Pages today" v={profile.pages_generated_today ?? 0} />
                <Field k="Pages this month" v={profile.pages_generated_this_month ?? 0} />
                <Field k="Annual plan" v={profile.is_annual_plan ? "Yes" : "No"} />
              </dl>
            </Section>

            <Section title={`Recent projects (${detail.recent_projects.length})`}>
              {detail.recent_projects.length === 0 ? (
                <div className="text-sm text-slate-500">No projects yet.</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {detail.recent_projects.map((p) => (
                    <li key={p.project_id} className="flex items-center gap-3 border border-slate-100 rounded-lg p-2">
                      {typeof p.thumbnail === "string" && p.thumbnail.length > 0 ? (
                        <img src={p.thumbnail} alt="" className="w-12 h-12 rounded object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded bg-slate-100" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{p.prompt || "(no prompt)"}</div>
                        <div className="text-xs text-slate-500">
                          {fmtDate(p.created_at)} · {p.status || "—"}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title={`Payment history (${detail.payment_history.length})`}>
              {detail.payment_history.length === 0 ? (
                <div className="text-sm text-slate-500">No payments.</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {detail.payment_history.map((t) => (
                    <li key={t.session_id || t.transaction_id} className="flex items-center justify-between border border-slate-100 rounded-lg p-2">
                      <div className="min-w-0">
                        <div className="font-medium">
                          {t.plan || "—"} · ${t.amount ?? 0}
                        </div>
                        <div className="text-xs text-slate-500">
                          {fmtDate(t.created_at)} · {t.is_annual ? "annual" : "monthly"}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        t.status === "completed"
                          ? "bg-green-100 text-green-700"
                          : t.status === "failed"
                          ? "bg-red-100 text-red-700"
                          : "bg-slate-100 text-slate-600"
                      }`}>
                        {t.status || "unknown"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title={`Recent sessions (${detail.recent_sessions.length})`}>
              {detail.recent_sessions.length === 0 ? (
                <div className="text-sm text-slate-500">No sessions on record.</div>
              ) : (
                <ul className="space-y-1 text-xs text-slate-600">
                  {detail.recent_sessions.map((s, i) => (
                    <li key={i} className="border border-slate-100 rounded p-2">
                      created {fmtDate(s.created_at)} · expires {fmtDate(s.expires_at)}
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        )}

        {modal && ready && (
          <ActionModal
            type={modal.type}
            user={profile}
            onClose={() => setModal(null)}
            onDone={() => {
              setModal(null);
              if (modal.type === "delete") {
                onAction();
                onClose();
              } else {
                fetchDetail();
                onAction();
              }
            }}
          />
        )}
      </aside>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-slate-700 mb-2">{title}</h3>
      {children}
    </section>
  );
}

function Field({ k, v, mono = false }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{k}</dt>
      <dd className={`text-slate-800 ${mono ? "font-mono text-xs" : ""}`}>{typeof v === "string" || typeof v === "number" ? v : v || "—"}</dd>
    </div>
  );
}

// ---------- Action modals ------------------------------------------------------

function ActionModal({ type, user, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [tier, setTier] = useState(user.subscription_tier || "free");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Reason is required (min 3 chars)");
      return;
    }
    setSubmitting(true);
    try {
      if (type === "tier") {
        await apiClient.patch(`/admin/users/${user.user_id}`, { tier, reason });
        toast.success(`Tier changed to ${tier}`);
      } else if (type === "suspend") {
        await apiClient.patch(`/admin/users/${user.user_id}`, { status: "suspended", reason });
        toast.success("User suspended");
      } else if (type === "unsuspend") {
        await apiClient.patch(`/admin/users/${user.user_id}`, { status: "active", reason });
        toast.success("User reactivated");
      } else if (type === "delete") {
        if (confirmEmail.trim().toLowerCase() !== user.email.toLowerCase()) {
          toast.error(`Type "${user.email}" to confirm deletion`);
          setSubmitting(false);
          return;
        }
        await apiClient.delete(`/admin/users/${user.user_id}`, {
          data: { confirm_email: confirmEmail.trim(), reason },
        });
        toast.success("User deleted");
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  const title = {
    tier: "Change tier",
    suspend: "Suspend user",
    unsuspend: "Reactivate user",
    delete: "Delete user",
  }[type];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="admin-action-modal">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">{title}</h2>
        <p className="text-xs text-slate-500 mb-4 break-all">{user.email}</p>

        {type === "tier" && (
          <div className="mb-4">
            <label className="text-xs text-slate-500 mb-1 block">New tier</label>
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="admin-modal-tier-select"
            >
              {["free", "basic", "plus", "pro"].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        )}

        {type === "delete" && (
          <div className="mb-4">
            <label className="text-xs text-red-700 mb-1 block">
              Type <span className="font-mono">{`delete ${user.email}`}</span> to confirm (just the email is enough — we compare emails only):
            </label>
            <input
              type="text"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={user.email}
              className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm"
              data-testid="admin-modal-confirm-email"
            />
          </div>
        )}

        <div className="mb-4">
          <label className="text-xs text-slate-500 mb-1 block">Reason (required)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="admin-modal-reason"
          />
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="admin-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className={`px-4 py-2 rounded-lg text-sm text-white ${
              type === "delete" ? "bg-red-600" : "bg-slate-900"
            }`}
            data-testid="admin-modal-confirm"
          >
            {submitting ? "Working…" : type === "delete" ? "Delete permanently" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
